import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  compose,
  setPropTypes,
  withState,
  lifecycle,
  withHandlers
} from 'recompose';
import { withRouter } from 'react-router';
import moment from 'moment-timezone';
import { withStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Header from '../../components/Header';
import MenuDrawer from '../../components/MenuDrawer';
import SearchButton from '../../components/atoms/SearchButton';
import CsvDownloadButton from '../../components/atoms/CsvDownloadButton';
import DepositTable from '../organisms/DepositTable';
import ProgressDialog from '../../components/molecules/ProgressDialog';
import { getDeposits, downloadDeposit } from '../../actions/Deposits';
import { isAuthenticated, sendLogin } from '../../auth/Auth';

const styles = {
  root: {
    backgroundColor: '#e0e0e0',
    flexGrow: 1,
    width: '100%'
  },
  pageTitileDiv: {
    paddingLeft: 20,
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff'
  },
  mainPaper: {
    position: 'relative',
    margin: 20
  },
  zoom: {
    position: 'relative',
    left: -10,
    top: 20
  },
  mainPaperTitleDiv: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '20px 26px 10px 26px'
  },
  searchForm: {
    padding: '10px 26px 10px 26px'
  },
  textField: {
    padding: '0px 5px 0px 5px',
    width: '300px'
  },
  textField150: {
    padding: '0px 5px 0px 5px',
    width: '200px'
  },
  button: {
    margin: '5px 5px'
  },
  buttonLeft: {
    textAlign: 'left',
    float: 'left'
  },
  buttonRight: {
    textAlign: 'right'
  }
};

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      account: state.accounts.account,
      depositStatuses: state.deposits.depositStatuses
    }),
    dispatch => ({
      actions: bindActionCreators({ getDeposits, downloadDeposit }, dispatch)
    })
  ),

  setPropTypes(propTypes),
  withState('openModal', 'updateOpenModal', false),
  withState('openDrawer', 'updateOpenDrawer', false),
  withState('openTable', 'updateOpenTable', false),
  withState('openProgress', 'updateOpenProgress', false),
  withState('loading', 'updateLoading', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('groupName', 'setGroupName', ''),
  withState('shopName', 'setShopName', ''),
  withState('depositDateFrom', 'setDepositDateFrom', () => {
    moment.tz.setDefault('Asia/Tokyo');
    return moment()
      .date(1)
      .format('YYYY-MM-DD');
  }),
  withState('depositDateTo', 'setDepositDateTo', () => {
    return moment()
      .endOf('month')
      .format('YYYY-MM-DD');
  }),
  withState('gochiDateFrom', 'setGochiDateFrom', ''),
  withState('gochiDateTo', 'setGochiDateTo', ''),
  withState('depositStatus', 'setDepositStatus', ''),
  withState('page', 'setPage', 0),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    },
    handleChangePage: props => (event, page) => {
      const table = document.getElementById('scrollTableId');
      if (table !== null) {
        table.scrollTop = 0;
      }
      props.setPage(page);
    }
  }),
  lifecycle({
    async componentDidMount() {
      isAuthenticated(this.props);
      const depositDateParam = {
        depositDateFrom: this.props.depositDateFrom,
        depositDateTo: this.props.depositDateTo
      };
      const depositData = await this.props.actions
        .getDeposits(depositDateParam)
        .then(data => this.props.checkSession(data));
      if (depositData.type === 'GET_DEPOSITS_SUCCESS') {
        this.props.updateOpenTable(true);
      }
    }
  })
);

const Main = enhance(
  ({
    classes,
    actions,
    checkSession,
    handleChangePage,
    account,
    depositStatuses,
    openDrawer,
    updateOpenDrawer,
    openTable,
    updateOpenTable,
    loading,
    updateLoading,
    openProgress,
    updateOpenProgress,
    resultMessage,
    setResultMessage,
    groupName,
    setGroupName,
    shopName,
    setShopName,
    depositDateFrom,
    setDepositDateFrom,
    depositDateTo,
    setDepositDateTo,
    gochiDateFrom,
    setGochiDateFrom,
    gochiDateTo,
    setGochiDateTo,
    depositStatus,
    setDepositStatus,
    page
  }) => {
    const isShop = typeof account !== 'undefined' && account.role === 'SHOP';
    const isGroup = typeof account !== 'undefined' && account.role === 'GROUP';
    const isSecretariat =
      typeof account !== 'undefined' && account.role === 'SECRETARIAT';
    const isDeveloper =
      typeof account !== 'undefined' && account.role === 'DEVELOPER';
    const roles = {
      isShop,
      isGroup,
      isSecretariat,
      isDeveloper
    };

    const searchKey = {
      groupName,
      shopName,
      depositDateFrom,
      depositDateTo,
      gochiDateFrom,
      gochiDateTo,
      depositStatus
    };

    const searchAction = async () => {
      updateLoading(true);
      updateOpenProgress(true);
      await actions
        .getDeposits(searchKey)
        .then(data => {
          checkSession(data);
          updateOpenProgress(false);
          setResultMessage('');
          updateLoading(false);
          handleChangePage(null, 0);
          updateOpenTable(true);
        })
        .catch(() => {
          updateOpenProgress(false);
          setResultMessage('');
          updateLoading(false);
        });
    };

    const downloadCsvBlob = (blob, fileName) => {
      if (window.navigator.msSaveOrOpenBlob) {
        // for IE,Edge
        window.navigator.msSaveOrOpenBlob(blob, fileName);
      } else {
        // for chrome, firefox
        const url = URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
        const linkEl = document.createElement('a');
        linkEl.href = url;
        linkEl.setAttribute('download', fileName);
        document.body.appendChild(linkEl);
        linkEl.click();

        linkEl.parentNode.removeChild(linkEl);
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 1000);
      }
    };

    const csvDownloadAction = async () => {
      updateLoading(true);
      updateOpenProgress(true);
      const response = await actions
        .downloadDeposit(searchKey)
        .then(data => checkSession(data));
      if (response.type === 'DOWNLOAD_DEPOSIT_SUCCESS') {
        const timeStamp = moment()
          .tz('Asia/Tokyo')
          .format('YYYYMMDDHHmm');
        updateOpenProgress(false);
        setResultMessage('');
        updateLoading(false);
        downloadCsvBlob(response.payload, `${timeStamp}_deposit_info.csv`);
      } else {
        setResultMessage('入金情報ダウンロードに失敗しました。');
        updateLoading(false);
      }
    };

    const progressOkAction = () => {
      updateOpenProgress(false);
    };

    return (
      <div className={classes.root}>
        <Header
          onMenuClick={() => {
            updateOpenDrawer(true);
          }}
        />
        <Drawer
          open={openDrawer}
          anchor="left"
          onClose={() => {
            updateOpenDrawer(false);
          }}
        >
          <div tabIndex={0} role="button">
            <MenuDrawer account={account} />
          </div>
        </Drawer>
        <div className={classes.pageTitileDiv}>
          <Typography variant="h6" color="textSecondary">
            入金管理・・・「売上受取方法」にStripeを選択した方だけが利用できる画面です。詳しくは「さきめし/ごちめし店舗運用マニュアル」を参照願います。
          </Typography>
        </div>
        <Paper className={classes.mainPaper} elevation={1}>
          <div className={classes.searchForm}>
            {(roles.isGroup || roles.isSecretariat || roles.isDeveloper) && (
              <div>
                {(roles.isSecretariat || roles.isDeveloper) && (
                  <TextField
                    label="運営会社名"
                    placeholder="部分一致"
                    type="search"
                    className={classes.textField}
                    margin="normal"
                    onChange={e => {
                      setGroupName(e.target.value);
                    }}
                  />
                )}
                {(roles.isGroup ||
                  roles.isSecretariat ||
                  roles.isDeveloper) && (
                  <TextField
                    label="店舗名"
                    placeholder="部分一致"
                    type="search"
                    className={classes.textField}
                    margin="normal"
                    onChange={e => {
                      setShopName(e.target.value);
                    }}
                  />
                )}
              </div>
            )}
            <div>
              <TextField
                label="入金日(From)"
                type="date"
                className={classes.textField150}
                margin="normal"
                defaultValue={depositDateFrom}
                InputLabelProps={{
                  shrink: true
                }}
                onChange={e => {
                  setDepositDateFrom(e.target.value);
                }}
              />
              <TextField
                label="入金日(To)"
                type="date"
                className={classes.textField150}
                margin="normal"
                defaultValue={depositDateTo}
                InputLabelProps={{
                  shrink: true
                }}
                onChange={e => {
                  setDepositDateTo(e.target.value);
                }}
              />
              <TextField
                label="ごち日(From)"
                type="date"
                className={classes.textField150}
                margin="normal"
                InputLabelProps={{
                  shrink: true
                }}
                onChange={e => {
                  setGochiDateFrom(e.target.value);
                }}
              />
              <TextField
                label="ごち日(To)"
                type="date"
                className={classes.textField150}
                margin="normal"
                InputLabelProps={{
                  shrink: true
                }}
                onChange={e => {
                  setGochiDateTo(e.target.value);
                }}
              />
            </div>
            <div>
              <TextField
                select
                label="入金状況"
                className={classes.textField}
                margin="normal"
                value={depositStatus}
                onChange={e => {
                  setDepositStatus(e.target.value);
                }}
                InputLabelProps={{
                  shrink: true
                }}
              >
                {depositStatuses.map(type => {
                  return (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  );
                })}
              </TextField>
            </div>
            <div className={classes.buttonLeft}>
              <SearchButton onClickAction={searchAction} />
            </div>
            <div className={classes.buttonRight}>
              <CsvDownloadButton onClickAction={csvDownloadAction} />
            </div>
          </div>
          {openTable && (
            <DepositTable
              roles={roles}
              page={page}
              handleChangePage={handleChangePage}
            />
          )}
        </Paper>
        <ProgressDialog
          openProgress={openProgress}
          progressOkAction={progressOkAction}
          loading={loading}
          resultMessage={resultMessage}
        />
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
